import {
  Button,
  Icon,
  InfoCircleHollowSVG,
  CheckCircleFilledSVG,
  CloseCircleFilledSVG,
} from "@newsela/angelou";
import {
  withTrackEvent,
  objectTypes,
  actionPrefixes,
  actionNames,
} from "@newsela/snitch";

import {
  ICheckForUnderstandingQuizBody,
  ICheckForUnderstandingQuizAnswerOption,
} from "@/types/models/CheckForUnderstandingQuiz.interface";
import { RevealButton } from "../RevealButton";
import { SubmitButton } from "../SubmitButton";
import {
  answerButtonWrapperCss,
  answersCss,
  bannerCss,
  cardCss,
  isCorrectBtnCss,
  isIncorrectBtnCss,
  infoCircleHollowSvgCss,
  optionBtnCss,
  optionTextCss,
  checkCircleFilledSvgCss,
  closeCircleFilledSvgCss,
} from "./ChecksForUnderstandingBody.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

const ChecksForUnderstandingBanner = () => (
  <div css={bannerCss}>
    <Icon
      SvgComponent={InfoCircleHollowSVG}
      size={Icon.sizes.md}
      isDecorative={false}
      title="locked"
      __cssFor={{ root: infoCircleHollowSvgCss }}
    />
    <span>
      When assigned, students will have unlimited tries to answer this question.
      The rest of the text will be hidden until the questions are answered
      correctly.
    </span>
  </div>
);

const letters = ["A", "B", "C", "D"];

export const ChecksForUnderstandingBody = ({
  currentFontSize,
  data,
  hasReadAloudInSession,
  isListenModeActive,
  isPresentModeActive = false,
  isStudentPreviewOn = false,
  isTeacher,
  selectedAnswer,
  setIsReadAloudModeActive,
  setSelectedAnswer,
  setSubmittedAnswers,
  submittedAnswers,
}: ICheckForUnderstandingQuizBody) => {
  const { questionData, quizId, assignmentId, contentId } = data;
  if (!questionData) return null;
  const options = questionData?.options;
  const question = questionData?.question;

  const quizSubmissionPayload = {
    quiz_id: quizId,
    assignment_content_id: assignmentId,
    article_content_id: contentId,
    quiz_question_id: questionData.id,
  };
  const selectedAnswers = selectedAnswer?.id
    ? submittedAnswers.concat([selectedAnswer])
    : submittedAnswers;

  const isCorrectAnswerVisible = Boolean(
    (isTeacher && !isPresentModeActive && !isStudentPreviewOn) ||
      submittedAnswers.find(
        (answer: ICheckForUnderstandingQuizAnswerOption) => answer.is_correct,
      ),
  );

  const isIncorrectAnswer = (
    option: ICheckForUnderstandingQuizAnswerOption,
  ) => {
    return Boolean(
      submittedAnswers.find(
        (answer) => answer.id === option.id && !answer.is_correct,
      ),
    );
  };

  const getButtonCss = (option: ICheckForUnderstandingQuizAnswerOption) => {
    if (option.is_correct && isCorrectAnswerVisible) {
      return isCorrectBtnCss;
    }

    if (
      submittedAnswers.find(
        (answer) => answer.id === option.id && !answer.is_correct,
      )
    ) {
      return isIncorrectBtnCss;
    }

    return optionBtnCss({
      isSelected: selectedAnswer?.id === option?.id,
    });
  };

  const TeacherFooter = (isPresentModeActive: boolean) =>
    isPresentModeActive ? (
      <RevealButton
        correctAnswer={options.find((option) => option.is_correct)}
        setSubmittedAnswers={setSubmittedAnswers}
      />
    ) : (
      <ChecksForUnderstandingBanner />
    );

  return (
    <div css={cardCss}>
      {question}
      <ol css={answersCss}>
        {options?.map(
          (option: ICheckForUnderstandingQuizAnswerOption, idx: number) => (
            <li key={option?.id}>
              <div css={answerButtonWrapperCss}>
                <ButtonWithTrackEvent
                  actionName={actionNames.select}
                  actionPrefix={actionPrefixes.initiate}
                  dataProps={{
                    "data-qa-selector":
                      "checks_for_understanding_quiz_answer_option",
                  }}
                  objectType={objectTypes.quizAnswer}
                  streamProperties={{
                    selected_option: option.text || "",
                  }}
                  isFullWidth
                  onClick={() => {
                    (!isTeacher || isStudentPreviewOn) &&
                      setSelectedAnswer(option);
                  }}
                  // Disable all options once correct answer has been selected
                  disabled={isIncorrectAnswer(option) || isCorrectAnswerVisible}
                  flavor={Button.flavors.polarMuted}
                  __cssFor={getButtonCss(option)}
                >
                  <div
                    css={optionTextCss({
                      currentFontSize,
                      isWrongAnswerVisible: isIncorrectAnswer(option),
                    })}
                  >
                    <span>{letters[idx] ?? "X"}</span>
                    <span>{option?.text}</span>
                  </div>
                  {option?.is_correct && isCorrectAnswerVisible && (
                    <Icon
                      SvgComponent={CheckCircleFilledSVG}
                      size={Icon.sizes.md}
                      isDecorative={false}
                      title="correct"
                      __cssFor={{ root: checkCircleFilledSvgCss }}
                    />
                  )}
                  {isIncorrectAnswer(option) && (
                    <Icon
                      SvgComponent={CloseCircleFilledSVG}
                      size={Icon.sizes.md}
                      isDecorative={false}
                      title="incorrect"
                      __cssFor={{ root: closeCircleFilledSvgCss }}
                    />
                  )}
                </ButtonWithTrackEvent>
              </div>
            </li>
          ),
        )}
      </ol>
      {isTeacher && !isStudentPreviewOn ? (
        TeacherFooter(isPresentModeActive)
      ) : (
        <SubmitButton
          currentFontSize={currentFontSize}
          hasReadAloudInSession={hasReadAloudInSession}
          setIsReadAloudModeActive={setIsReadAloudModeActive}
          setSubmittedAnswers={setSubmittedAnswers}
          submittedAnswers={submittedAnswers}
          selectedAnswers={selectedAnswers}
          selectedAnswer={selectedAnswer}
          quizSubmissionPayload={quizSubmissionPayload}
          isListenModeActive={isListenModeActive}
          isTeacher={isTeacher}
          isCorrectAnswerVisible={isCorrectAnswerVisible}
          isStudentPreviewOn={isStudentPreviewOn}
        />
      )}
    </div>
  );
};
